/**
 * گرافِ صفحه‌به‌صفحه — تبِ دومِ «جریانِ سفر».
 *
 * چیدمان و اعداد کارِ `@/core/lib/journeyGraph` است و این فایل فقط SVG
 * می‌سازد — همان تقسیمِ کاری که `SankeyChart` با `lib/sankey` دارد: ریاضی
 * با `npm test` آزموده می‌شود، شکل این‌جا می‌ماند.
 *
 * ═══ چه چیزی این‌جا هست که در سنکی نبود ═══
 *
 * سنکی ستون‌هایش «پله»اند، پس `/discover` در پله‌ی دوم و پله‌ی چهارم دو گرهِ
 * جدا است. این‌جا هر آدرس **یک** گره است، یعنی می‌شود پرسید «روی هم چند نفر
 * وارد کشف شدند» و می‌شود حلقه دید (کشف → رویداد → کشف) — چیزی که سنکی
 * ذاتاً نمی‌کشد.
 *
 * ═══ ⚠ گرهِ خروج عمداً نیست ═══
 *
 * یک گرهِ خروجِ مشترک که همه‌ی صفحه‌ها به آن وصل باشند، وسطِ گراف یک ستاره‌ی
 * پر از یال می‌سازد و بقیه‌ی شکل را می‌خورد. پس خروج **رنگِ خودِ گره** است:
 * سبز یعنی هیچ‌کس از این صفحه بیرون نرفت، قرمز یعنی همه، و وسطِ طیف خنثی.
 * گرهِ «ورود» ولی هست، چون یالِ واقعیِ «نشست از کجا شروع شد» بدونش گم می‌شد.
 *
 * ⚠ رنگ‌ها از `exitColor` می‌آیند و هیچ‌کدام ثابت نیستند — `color-mix` روی
 * توکن‌های تم. رنگِ ثابت در `apps/admin` ممنوع است و در تمِ تیره هم می‌شکست.
 *
 * ═══ جابه‌جا کردنِ گره‌ها با موس ═══
 *
 * هر چیدنِ خودکاری روی یک دادهٔ واقعی چند جا بد می‌افتد، و کسی که به گراف
 * نگاه می‌کند خودش می‌داند کدام دو صفحه باید کنارِ هم باشند. پس گره‌ها
 * کشیدنی‌اند و جای تازه‌شان در همین مرورگر می‌ماند.
 *
 * ⚠ مختصاتِ موس با `getScreenCTM()` به مختصاتِ داخلیِ SVG برمی‌گردد و نه با
 * نسبت‌گیریِ دستی از `getBoundingClientRect`: نمودار با `viewBox` کش می‌آید،
 * پس یک پیکسلِ صفحه با یک واحدِ داخلی یکی نیست و آن ضریب با هر تغییرِ عرضِ
 * پنجره عوض می‌شود. همان تله‌ای که سرِ `SankeyChart` هم نوشته شده.
 */
import React, { useRef, useState } from 'react';
import {
  ENTRY, buildJourneyGraph, exitColor, layoutJourneyGraph,
  type LaidOutGraphNode,
} from '@/core/lib/journeyGraph';
import type { JourneyData } from '@/core/lib/journeyReport';
import type { SurfaceFilter } from '@/core/lib/activityReport';
import { fmtNum as fmt } from '@/ui/Num';

const pct = (x: number): string => `${Math.round(x * 100)}٪`;

type Spot = { x: number; y: number };
type Spots = Record<string, Spot>;

/**
 * چیدمانِ دستی در همین مرورگر می‌ماند.
 *
 * ⚠ مثلِ بقیه‌ی کلیدهای مرورگر، پیشوندش `whosup.` است و عوض نمی‌شود —
 * عوض کردنِ نامِ کلید دادهٔ کسی را منتقل نمی‌کند، **گمش می‌کند**
 * (فهرستِ کلیدها در CLAUDE.md).
 */
const SPOTS_KEY = 'whosup.journeyGraph.v1';

const loadSpots = (): Spots => {
  try {
    const raw = localStorage.getItem(SPOTS_KEY);
    return raw ? (JSON.parse(raw) as Spots) : {};
  } catch {
    // مرورگری که ذخیره‌سازی را بسته یا مقدارِ خرابی که مانده — چیدمانِ
    // خودکار جوابِ درستی است، ترکاندنِ سربرگ نه.
    return {};
  }
};

const saveSpots = (s: Spots): void => {
  try {
    if (Object.keys(s).length) localStorage.setItem(SPOTS_KEY, JSON.stringify(s));
    else localStorage.removeItem(SPOTS_KEY);
  } catch { /* ذخیره نشد؛ چیدمان تا وقتی صفحه باز است سرِ جایش می‌ماند. */ }
};

/** آستانه‌های یالِ نازک. «همه» پیش‌فرض است؛ بقیه برای وقتی که گراف شلوغ شود. */
const THRESHOLDS = [
  { v: 1, label: 'همه‌ی یال‌ها' },
  { v: 2, label: '۲ به بالا' },
  { v: 5, label: '۵ به بالا' },
];

/** نامِ گره، کوتاه‌شده تا زیرِ دایره جا شود. کاملش در کارتِ شناور است. */
const short = (id: string): string =>
  (id === ENTRY ? 'ورود' : id.length > 20 ? `${id.slice(0, 19)}…` : id);

interface Hover { node: LaidOutGraphNode; x: number; y: number; flip: boolean }

/** گرهی که همین حالا زیرِ انگشت/موس است، با فاصله‌ی گرفتنش تا مرکزِ گره. */
interface Drag { id: string; dx: number; dy: number; moved: boolean }

/* ── اندازه‌های کادر ── */

const W = 900;
/** قلمروِ چیدنِ خودکار — باریک‌تر از `W`، برای حاشیه‌ی برچسب‌ها. */
const LAYOUT_W = 820;
const PAD_T = 26;
/** جا برای برچسب و عددِ زیرِ پایین‌ترین دایره. */
const PAD_B = 34;
/**
 * ⚠ چهل پیکسل حاشیه‌ی چپ و راست برای **برچسب** است و نه برای دایره: برچسب
 * زیرِ گره وسط‌چین است، پس نیمی‌اش از لبه‌ی ستونِ اول می‌زند بیرون و بی این
 * حاشیه بریده می‌شد. ۸۲۰ + ۸۰ = همان ۹۰۰.
 */
const PAD_X = 40;

/**
 * تا کجا می‌شود گره را کشید — کلِ ناحیه‌ی قابلِ دیدن منهای کمی حاشیه، و نه
 * فقط قلمروِ چیدنِ خودکار. چرایش سرِ `GraphLayoutOptions.bounds`.
 */
const DRAG_BOUNDS = { x0: -PAD_X + 6, y0: 2, x1: LAYOUT_W + PAD_X - 6, y1: 0 };

export function JourneyGraph(
  { data, surface = 'all' }: { data: JourneyData; surface?: SurfaceFilter },
) {
  const wrap = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const [minEdge, setMinEdge] = useState(1);
  const [spots, setSpots] = useState<Spots>(loadSpots);
  const spotsRef = useRef<Spots>(spots);
  const drag = useRef<Drag | null>(null);

  // ⚠ `surface` باید این‌جا هم برود: تبِ سنکیِ کنارِ همین گراف فیلترِ سطحِ
  // بالای سربرگ را اعمال می‌کند، و دو تبِ یک کارت که دو عدد بدهند از هر
  // کدامشان به‌تنهایی بدترند (WHOSUP-242).
  const g = React.useMemo(
    () => buildJourneyGraph(data, { minEdge, surface }),
    [data, minEdge, surface],
  );

  /**
   * ارتفاع از **شلوغ‌ترین ستون** می‌آید و نه ثابت: با ارتفاعِ ثابت، ستونی
   * که ده گره دارد دایره‌هایش روی هم می‌افتند و برچسبِ هرکدام روی گرهِ
   * بعدی می‌نشیند.
   */
  const laid = React.useMemo(() => {
    if (!g.nodes.length) return null;
    const perCol = new Map<number, number>();
    for (const n of g.nodes) perCol.set(n.depth, (perCol.get(n.depth) ?? 0) + 1);
    const tallest = Math.max(...perCol.values());
    const height = Math.max(320, tallest * 84);
    // شعاع نباید از نصفِ فاصله‌ی دو گرهِ هم‌ستون بزرگ‌تر شود، وگرنه دو
    // دایره‌ی بزرگِ هم‌ستون هم را می‌پوشانند.
    const maxR = Math.max(12, Math.min(30, height / tallest / 2 - 16));
    return layoutJourneyGraph(g.nodes, g.edges, {
      width: LAYOUT_W, height, maxR, positions: spots,
      // پایینِ کادرِ کشیدن همان ته نمودار است، که با ارتفاعِ داده عوض می‌شود.
      bounds: { ...DRAG_BOUNDS, y1: height - 2 },
    });
  }, [g, spots]);

  if (!laid) return <p className="dim">—</p>;

  const track = (n: LaidOutGraphNode) => (e: React.MouseEvent) => {
    if (drag.current) return;
    const box = wrap.current?.getBoundingClientRect();
    if (!box) return;
    const x = e.clientX - box.left;
    setHover({ node: n, x, y: e.clientY - box.top, flip: x > box.width * 0.55 });
  };

  /** مختصاتِ اشاره‌گر، در همان دستگاهی که چیدمان با آن کار می‌کند. */
  const toLocal = (e: React.PointerEvent): Spot | null => {
    const svg = svgRef.current;
    const m = svg?.getScreenCTM();
    if (!svg || !m) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(m.inverse());
    return { x: p.x - PAD_X, y: p.y - PAD_T };
  };

  const grab = (n: LaidOutGraphNode) => (e: React.PointerEvent) => {
    // فقط دکمه‌ی اصلی. راست‌کلیک نباید گره را بردارد.
    if (e.button !== 0) return;
    const p = toLocal(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: n.id, dx: n.x - p.x, dy: n.y - p.y, moved: false };
    setHover(null);
  };

  const move = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const p = toLocal(e);
    if (!p) return;
    d.moved = true;
    // ⚠ آخرین مقدار از یک `ref` می‌آید و نه از تابعِ به‌روزرسانیِ حالت:
    // نوشتن در `localStorage` **داخلِ** آن تابع یک عارضه است و ری‌اکت اجازه
    // دارد دو بار صدایش بزند.
    const next = { ...spotsRef.current, [d.id]: { x: p.x + d.dx, y: p.y + d.dy } };
    spotsRef.current = next;
    setSpots(next);
  };

  const drop = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    // ⚠ فقط وقتی واقعاً کشیده شده ذخیره می‌شود. یک کلیکِ ساده نباید گره را
    // «دستی‌چیده» علامت بزند، وگرنه دکمه‌ی «چیدمانِ خودکار» بی‌خود روشن
    // می‌ماند و چیدمانِ محاسبه‌شده دیگر به‌روز نمی‌شود.
    if (d.moved) saveSpots(spotsRef.current);
  };

  const reset = () => { spotsRef.current = {}; setSpots({}); saveSpots({}); };
  const moved = Object.keys(spots).length;

  return (
    <>
      <div className="chiprow" style={{ marginBottom: 8 }}>
        {THRESHOLDS.map((t) => (
          <button key={t.v} type="button"
            className={`chip${minEdge === t.v ? ' on' : ''}`}
            onClick={() => setMinEdge(t.v)}>{t.label}</button>
        ))}
        {/* آنچه کنار رفته هیچ‌وقت بی‌صدا نیست. */}
        {g.hiddenEdges.count ? (
          <span className="dim">
            {fmt(g.hiddenEdges.count)} یال کنار رفت ({fmt(g.hiddenEdges.transitions)} گذر)
          </span>
        ) : null}
        {/* فقط وقتی چیزی برای برگرداندن هست — دکمه‌ی همیشه‌روشن یعنی آدم
            نمی‌فهمد چیدمان دستی است یا خودکار. */}
        {moved ? (
          <>
            <span className="spacer" />
            <span className="dim">{fmt(moved)} گره دستی جابه‌جا شده</span>
            <button type="button" className="chip" onClick={reset}>
              چیدمانِ خودکار
            </button>
          </>
        ) : null}
      </div>

      <div className="sankeywrap" ref={wrap} onMouseLeave={() => setHover(null)}>
        <svg className="jgraph" ref={svgRef}
          viewBox={`0 0 ${W} ${laid.height + PAD_T + PAD_B}`}
          role="img" aria-label="گرافِ صفحه‌به‌صفحه‌ی سفرِ کاربر">
          <g transform={`translate(${PAD_X},${PAD_T})`}>
            {/* یال‌ها اول، تا گره‌ها رویشان بنشینند. */}
            {laid.edges.map((e) => (
              <g key={`${e.from}>${e.to}`} className={`edgeg${e.back ? ' back' : ''}`}>
                <path className="edge" d={e.path} strokeWidth={e.thickness}>
                  <title>{`${e.from} → ${e.to}: ${fmt(e.value)}`}</title>
                </path>
                {/* سرِ پیکان وسطِ منحنی می‌نشیند و نه تهش — چرایش سرِ
                    `LaidOutGraphEdge.arrow` نوشته شده. */}
                <path className="arrow" d="M-5.5,-4.5 L6,0 L-5.5,4.5 Z"
                  transform={`translate(${e.arrow.x},${e.arrow.y}) rotate(${e.arrow.deg})`}>
                  <title>{`${e.from} → ${e.to}: ${fmt(e.value)}`}</title>
                </path>
              </g>
            ))}

            {laid.nodes.map((n) => (
              <g key={n.id} onMouseMove={track(n)}
                onPointerDown={grab(n)} onPointerMove={move}
                onPointerUp={drop} onPointerCancel={drop}
                className={hover?.node.id === n.id ? 'nodeg on' : 'nodeg'}>
                {/* ⚠ ناحیه‌ی حساسِ موس یک دایره‌ی **نامرئیِ** بزرگ‌تر است و
                    نه خودِ گره: گرهِ کم‌ورودی هفت پیکسل شعاع دارد و گرفتنش
                    با موس تقریباً ناممکن بود — همان درسی که `SankeyChart`
                    با مستطیلِ `hit` گرفته بود. این‌جا دستگیره‌ی کشیدن هم
                    هست، پس بزرگ بودنش دو بار به کار می‌آید. */}
                <circle className="hit" cx={n.x} cy={n.y} r={Math.max(n.r, 17)} />
                <circle className={`gnode${n.id === ENTRY ? ' entry' : ''}`}
                  cx={n.x} cy={n.y} r={n.r} style={{ fill: exitColor(n.exitRate) }} />
                <text x={n.x} y={n.y + n.r + 12}>{short(n.id)}</text>
                <text className="v" x={n.x} y={n.y + n.r + 23}>
                  {fmt(n.id === ENTRY ? n.outbound : n.inbound)}
                </text>
              </g>
            ))}
          </g>
        </svg>

        {hover ? (
          <div className="sankeytip"
            style={hover.flip
              ? { right: `calc(100% - ${hover.x - 14}px)`, top: hover.y + 12 }
              : { left: hover.x + 14, top: hover.y + 12 }}>
            <div className="tiphead mono ltr">{hover.node.id}</div>
            {hover.node.id === ENTRY ? (
              <>
                <div className="tipnums">
                  <span><b>{fmt(hover.node.outbound)}</b> نشست</span>
                </div>
                <p>شروعِ نشست. صفحه نیست، پس نرخِ خروج ندارد.</p>
              </>
            ) : (
              <>
                <div className="tipnums">
                  <span><b>{fmt(hover.node.inbound)}</b> ورود</span>
                  <span className="dim"><b>{fmt(hover.node.sessions)}</b> نشست</span>
                  <span className="dim"><b>{fmt(hover.node.exits)}</b> خروج</span>
                </div>
                <p>
                  {pct(hover.node.exitRate ?? 0)} از ورودها همین‌جا تمام شد
                  {hover.node.outbound
                    ? ` و ${fmt(hover.node.outbound)} بار از این‌جا جای دیگری رفتند.`
                    : ' و از این‌جا هیچ‌کس به صفحه‌ی دیگری نرفت.'}
                  {' '}میانگینِ جایش در سفر پله‌ی{' '}
                  <b>{hover.node.meanStep.toFixed(1)}</b> است — ستونش از همین می‌آید.
                </p>
              </>
            )}
          </div>
        ) : null}
      </div>

      <GraphLegend />
    </>
  );
}

/**
 * افسانه‌ی رنگ. طیف با همان `exitColor` ساخته می‌شود و نه با یک گرادیانِ
 * دستی — وگرنه اولین باری که طیف عوض شود، افسانه چیزِ دیگری می‌گوید.
 */
function GraphLegend() {
  const stops = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div className="jglegend">
      <span className="dim">هیچ‌کس خارج نشد</span>
      <span className="ramp">
        {stops.map((s) => (
          <i key={s} style={{ background: exitColor(s) }} />
        ))}
      </span>
      <span className="dim">همه خارج شدند</span>
    </div>
  );
}
